import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { CLIENT_TYPE, CURIOUS_JUNIOR } from 'src/constants/type';
import { setPlayerParams } from 'src/lib/redux';
import { PlayerAuthParamsState, PlayerParamsState } from 'src/types/playerParams.type';

import {
  authenticationParamMap,
  playerParamMap,
  PlayerParamMapItem,
  PlayerParamsAuthMapItem,
} from './utils';

// Generic shared helper function that works with any param map type
const parseParamsFromMap = <T extends Record<string, any>>(
  search: string,
  paramMap: PlayerParamMapItem[] | PlayerParamsAuthMapItem[],
): Partial<T> => {
  const params = new URLSearchParams(search);
  const result: Partial<T> = {};

  paramMap.forEach(({ stateKey, aliases, transform }) => {
    let value: string | null = null;
    for (const alias of aliases) {
      value = params.get(alias);
      if (value !== null && value !== undefined) break;
    }
    if (value !== null && value !== undefined) {
      (result as any)[stateKey] = transform ? transform(value) : value;
    }
  });

  return result;
};

export const parsePlayerAuthParams = (): Partial<PlayerAuthParamsState> => {
  return parseParamsFromMap<PlayerAuthParamsState>(window.location.search, authenticationParamMap);
};

export const parseAndMapPlayerParams = (search: string): Partial<PlayerParamsState> => {
  const result = parseParamsFromMap<PlayerParamsState>(search, playerParamMap);
  result.isCjrWebview = result.cameFrom === CURIOUS_JUNIOR && result.isWebview === true;
  result.isFromCjr = result.cameFrom === CURIOUS_JUNIOR;
  result.cameFromQBG = result.cameFrom?.toLowerCase() === CLIENT_TYPE.ADMIN;

  return result;
};

export function useInitPlayerParams() {
  const dispatch = useDispatch();
  useEffect(() => {
    const mappedParams = parseAndMapPlayerParams(window.location.search);

    // Check if source=OTT_DPP and replace vType with OTT_DPP in URL
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('source') === 'OTT_DPP' && urlParams.get('vType') !== 'OTT_DPP') {
      urlParams.set('vType', 'OTT_DPP');
      const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
      window.history.replaceState({}, '', newUrl);

      // Re-parse with updated URL
      const updatedParams = parseAndMapPlayerParams(urlParams.toString());
      dispatch(setPlayerParams(updatedParams));
    } else {
      dispatch(setPlayerParams(mappedParams));
    }
  }, [dispatch]);
}
